export interface NavigationTimingMetrics {
  pageLoadMs: number | null;
  domContentLoadedMs: number | null;
  ttfbMs: number | null;
}

function roundMs(value: number): number {
  return Math.round(value);
}

/**
 * Reads Navigation Timing Level 2 metrics for the current document.
 * pageLoadMs is null until window load has fired (loadEventEnd > 0).
 */
export function getNavigationTimingMetrics(): NavigationTimingMetrics {
  const empty: NavigationTimingMetrics = {
    pageLoadMs: null,
    domContentLoadedMs: null,
    ttfbMs: null,
  };

  if (typeof performance === 'undefined') {
    return empty;
  }

  try {
    const nav = performance.getEntriesByType(
      'navigation'
    )[0] as PerformanceNavigationTiming | undefined;

    if (!nav) {
      return empty;
    }

    const pageLoadMs =
      nav.loadEventEnd > 0
        ? roundMs(nav.loadEventEnd - nav.startTime)
        : null;

    const domContentLoadedMs =
      nav.domContentLoadedEventEnd > 0
        ? roundMs(nav.domContentLoadedEventEnd - nav.startTime)
        : null;

    const ttfbMs =
      nav.responseStart > 0
        ? roundMs(nav.responseStart - nav.startTime)
        : null;

    return { pageLoadMs, domContentLoadedMs, ttfbMs };
  } catch {
    return empty;
  }
}

/**
 * Resolves once load timing is available, or after timeoutMs (whichever first).
 * Still returns whatever metrics exist on timeout so PAGE_VIEW is not blocked forever.
 */
export function waitForNavigationTiming(
  timeoutMs = 15000
): Promise<NavigationTimingMetrics> {
  const current = getNavigationTimingMetrics();
  if (current.pageLoadMs !== null) {
    return Promise.resolve(current);
  }

  if (typeof window === 'undefined') {
    return Promise.resolve(current);
  }

  return new Promise((resolve) => {
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout>;
    let onLoad: () => void;

    const finish = (): void => {
      if (settled) {
        return;
      }
      settled = true;
      window.removeEventListener('load', onLoad);
      clearTimeout(timeoutId);
      resolve(getNavigationTimingMetrics());
    };

    // loadEventEnd is set after the load event handlers run
    onLoad = (): void => {
      setTimeout(finish, 0);
    };

    timeoutId = setTimeout(finish, timeoutMs);

    if (document.readyState === 'complete') {
      setTimeout(finish, 0);
    } else {
      window.addEventListener('load', onLoad, { once: true });
    }
  });
}
